import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, RotateCcwIcon } from 'lucide-react';
import { HookScreen } from './HookScreen';
import { ReframeScreen } from './ReframeScreen';
import { RevealScreen } from './RevealScreen';
import { ProofScreen } from './ProofScreen';
import { MiniSimScreen } from './MiniSimScreen';
import { PenScreen } from './PenScreen';
import { rekovelleStartingDose } from '../../utils/rekovelleDose';
type Answer = 'yes' | 'no' | null;
type TeaserSnakProps = {
  /** Screen to open on (0 Hook … 5 Proof). */
  initialScreen?: number;
  /** Render as a static still: seeded answer, paused pen clip. */
  still?: boolean;
};
export const TEASER_SCREENS = 6;
const EASE = [0.23, 1, 0.32, 1] as const;
const DEFAULT_AMH = 14;
const DEFAULT_WEIGHT = 65;

/** The ~40-second REKOVELLE teaser Snak, built for a 390px-wide phone screen. */
export function TeaserSnak({
  initialScreen = 0,
  still = false
}: TeaserSnakProps) {
  const reduced = useReducedMotion() ?? false;
  const [screen, setScreen] = useState(initialScreen);
  const [answer, setAnswer] = useState<Answer>(still ? 'no' : null);
  const [amh, setAmh] = useState(DEFAULT_AMH);
  const [weight, setWeight] = useState(DEFAULT_WEIGHT);
  const dose = rekovelleStartingDose(amh, weight);
  const next = () => setScreen(s => Math.min(TEASER_SCREENS - 1, s + 1));
  const back = () => setScreen(s => Math.max(0, s - 1));
  const replay = () => {
    setAnswer(null);
    setAmh(DEFAULT_AMH);
    setWeight(DEFAULT_WEIGHT);
    setScreen(0);
  };
  return <section aria-label="REKOVELLE teaser Snak" className="flex h-full flex-1 flex-col bg-white px-6 pb-5 pt-4 font-sans text-ink" data-id="element-712" data-mp="2rd8r6zlkcd7">
      <div className="flex items-center gap-3" data-id="element-713" data-mp="7rho2torr11u">
        <button type="button" onClick={back} disabled={screen === 0} aria-label="Previous screen" className="-ml-2 rounded-full p-1.5 text-graphite transition-[color,opacity] duration-150 hover:text-ink disabled:opacity-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-rx" data-id="element-714" data-mp="7s9k25ma30sn">
          <ChevronLeftIcon size={18} strokeWidth={2} data-id="element-715" data-mp="1vn6389dpx8gt" />
        </button>
        <div className="flex flex-1 gap-1.5" role="progressbar" aria-valuemin={1} aria-valuemax={TEASER_SCREENS} aria-valuenow={screen + 1} data-id="element-716" data-mp="1qokxxvjlzyli">
          {Array.from({
          length: TEASER_SCREENS
        }).map((_, i) => <span key={i} className="h-[3px] flex-1 overflow-hidden rounded-full bg-ink/10" data-id="element-717" data-mp="2je1mgi43yvcg" data-mpi={i}>
              <motion.span className="block h-full origin-left bg-ink" initial={false} animate={{
            scaleX: i <= screen ? 1 : 0
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} data-id="element-718" data-mp="2ee6z2ojjknw5" />
            </span>)}
        </div>
        <span className="w-7 text-right font-mono text-[10px] tracking-[0.1em] text-graphite" data-id="element-719" data-mp="324t6cdlq2453">
          {screen + 1}/{TEASER_SCREENS}
        </span>
        <button type="button" onClick={replay} disabled={screen === 0} aria-label="Replay from the start" title="Replay" className="-mr-2 rounded-full p-1.5 text-graphite transition-[color,opacity] duration-150 hover:text-ink disabled:opacity-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-rx" data-id="element-720" data-mp="2xc9srgn1b90a">
          <RotateCcwIcon size={16} strokeWidth={2} data-id="element-721" data-mp="xw9ay150w1xk" />
        </button>
      </div>

      <div className="relative mt-5 flex flex-1 flex-col" data-id="element-722" data-mp="12sq2z6fcf0fx">
        <AnimatePresence mode="wait" initial={false} data-id="element-723" data-mp="17svtgj9vmgwf">
          <motion.div key={screen} className="flex flex-1 flex-col [&>*]:flex-1" initial={{
          opacity: 0,
          x: reduced ? 0 : 16
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: reduced ? 0 : -16
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} data-id="element-724" data-mp="17rb375ulhk4d">
            {screen === 0 && <HookScreen onAnswer={a => {
            setAnswer(a);
            next();
          }} data-id="element-725" data-mp="1qiu0cw1xu3yh" />}
            {screen === 1 && <ReframeScreen answer={answer} onNext={next} data-id="element-726" data-mp="17xtj76k51zte" />}
            {screen === 2 && <MiniSimScreen amh={amh} weight={weight} onAmh={setAmh} onWeight={setWeight} onNext={next} data-id="element-727" data-mp="17vh1muialrgv" />}
            {screen === 3 && <RevealScreen amh={amh} weight={weight} dose={dose} onNext={next} data-id="element-728" data-mp="1hnmv68o70gy6" />}
            {screen === 4 && <PenScreen dose={dose} onNext={next} paused={still} data-id="element-729" data-mp="txiqrz36kd64" />}
            {screen === 5 && <ProofScreen onReplay={replay} data-id="element-730" data-mp="tzlpr8mb9gmz" />}
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="mt-4 text-center text-[9.5px] leading-snug text-graphite/70" data-id="element-731" data-mp="13vyio4a17qgo">
        Conceptual prototype for discussion. Clinical content and claims subject to Ferring medical/legal review.
      </p>
    </section>;
}